import * as React from 'react';
import {useEffect, useState, useMemo} from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material/utils';

const excludedCountryCodes = ['af', 'by', 'bt', 'bn', 'ck', 'tl', 'fj', 'ki', 'la', 'mh', 'fm', 'mm', 'nu', 'pw', 'pg', 'ru', 'sb', 'sy', 'to', 'tv', 'ua', 'vu', 'wf', 'sy', 'ye'];

export default function CustomAutoComplete({autoSelectValue, handleChange, isStatusDisplayed, error, ...props }) {
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);

  const fetchGeoapify = useMemo(
    () =>
      debounce(async (text, callback) => {
        try {
          const {data} = await axios.get(
            `https://api.geoapify.com/v1/geocode/autocomplete?text=${text}&type=street&format=json&apiKey=${process.env.REACT_APP_GEOAPIFY_API_KEY}`
          );
          const filteredResults = data.results.filter( 
            (result) => !excludedCountryCodes.includes(result?.country_code)
          );
          callback(filteredResults);
        } catch (error) {
          console.error('Error fetching Geoapify data:', error);
          callback([]);
        }
      }, 500),
    []
  );

  useEffect(() => {
    if (!inputValue) return;

    fetchGeoapify(inputValue, (addresses) => {
      let filteredAdresses = [];

      if (addresses)
        filteredAdresses = addresses?.map((address) => address?.formatted);

      if (value) 
        setOptions([value,...filteredAdresses]);
      else 
        setOptions(filteredAdresses); 
      
    });
  }, [fetchGeoapify, inputValue]);

  const borderColor = error ? '1px solid #F88989' : '1px solid #88DD60';
  const statusClassName = error ? 'status-error' : 'status-success';

  return (
    <Grid sx={{margin: '16px 0 0 0'}}>
      <Typography className='label-autocomplete'>
        Shipping Address
      </Typography>
      <Autocomplete
        {...props}
        value={value} 
        sx={{       
          borderRadius: '4px',
          border: isStatusDisplayed ?  borderColor : '1px solid #16161D',

          '&:hover' : {
            border: isStatusDisplayed ?  borderColor : "1px solid #2C2C3A",

          },
          '&.Mui-focused': {
            border: '1px solid #A073FF' 
          },
        }}
        getOptionLabel={(option) => option || ""}
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        noOptionsText="No locations"
        onChange={(event, newValue) => {                   
          handleChange(event, newValue);
          setValue(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);      
        }}
        onBlur={() => {   
          setOptions([]);
          if (inputValue !== '') {
            handleChange(null, inputValue);   
            setValue(inputValue);
          }          
        }}
        renderInput={(params) => (
          <TextField 
            {...params}  
            className={isStatusDisplayed ? statusClassName: ""}
            sx={{ 
              color: 'white',
              fontSize: '14px',
              backgroundColor: '#16161D',
              borderRadius: '4px',          
            }} 
            inputProps={{
              ...params.inputProps,
              style: {
                boxSizing: 'border-box',
                outline: 'none',
                padding: 0,
                border: 0,
                fontSize: '14px',
                color: '#FFF',
                fontFamily: 'Source Code Pro, monospace',
                fontWeight: 400,
                lineHeight: '20px'
              },
            }}          
            fullWidth 
          />
        )}
        renderOption={(props, option) => {
          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: 'flex', width: 44 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Grid>
                <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                  <Box
                    component="span"
                    sx={{ 
                      fontWeight: 'regular',
                      fontSize: '14px'                      
                    }}
                  >
                    {option}
                  </Box>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
      {isStatusDisplayed && error && <p className='autocomplete-error'>{error}</p>}
    </Grid>
  );
}